import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { Card, Row, Col, Form } from "react-bootstrap";

import { ResidenteService } from "services/ResidenteService";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import {
  notifySuccess,
  notifyError,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import {
  LinkPrimary,
  LinkDanger,
} from "jsx/components/CitoPlus/components/CitoPlusLink";

const validationSchema = Yup.object().shape({
  cedula: Yup.string()
    .matches(/^\d{6,10}$/, "Cédula debe tener entre 6 y 10 dígitos")
    .required("La cédula es obligatoria"),
  nombres: Yup.string()
    .min(3, "Nombres debe tener al menos 3 caracteres")
    .required("Los nombres son obligatorios"),
  whatsapp: Yup.string()
    .matches(/^57\d{10}$/, "WhatsApp debe tener formato 57XXXXXXXXXX")
    .required("El WhatsApp es obligatorio"),
  celular: Yup.string()
    .matches(/^57\d{10}$/, "Celular debe tener formato 57XXXXXXXXXX")
    .required("El celular es obligatorio"),
  correo: Yup.string()
    .email("Correo electrónico inválido")
    .required("El correo es obligatorio"),
  placa: Yup.string().matches(
    /^[A-Z0-9]{6}$|^$/,
    "La placa debe tener exactamente 6 caracteres alfanuméricos",
  ),
  fecha_inicio: Yup.date()
    .nullable()
    .required("La fecha de inicio es obligatoria"),
  fecha_final: Yup.date()
    .nullable()
    .min(
      Yup.ref("fecha_inicio"),
      "La fecha final debe ser posterior a la de inicio",
    )
    .required("La fecha final es obligatoria"),
  es_airbnb: Yup.boolean(),
  tipo_de_ingreso: Yup.string().required("El tipo de ingreso es obligatorio"),
  acompanantes: Yup.array().of(
    Yup.object({
      cedula: Yup.string().matches(
        /^$|^\d{6,10}$/,
        "Cédula debe tener entre 6-10 dígitos o estar vacía",
      ),
      nombres: Yup.string().min(
        0,
        "Nombres deben tener al menos 3 caracteres si se especifican",
      ),
    }).test(
      "acompanante-validation",
      "Si ingresa nombres, la cédula es requerida y viceversa",
      function (value) {
        const { cedula, nombres } = value || {};
        const hasCedula = cedula && cedula.trim() !== "";
        const hasNombres = nombres && nombres.trim() !== "";

        if (!hasCedula && !hasNombres) {
          return true;
        }

        if (hasCedula && !hasNombres) {
          return this.createError({
            path: `${this.path}.nombres`,
            message: "Nombres son requeridos cuando se ingresa cédula",
          });
        }

        if (hasNombres && !hasCedula) {
          return this.createError({
            path: `${this.path}.cedula`,
            message: "Cédula es requerida cuando se ingresan nombres",
          });
        }

        if (hasNombres && nombres.trim().length < 3) {
          return this.createError({
            path: `${this.path}.nombres`,
            message: "Nombres deben tener al menos 3 caracteres",
          });
        }

        return true;
      },
    ),
  ),
});

const tipoIngresoOptions = [
  { value: "0", label: "VISITANTE" },
  { value: "1", label: "DOMICILIO" },
  { value: "2", label: "CONTRATISTA" },
  { value: "3", label: "CLIENTE" },
  { value: "4", label: "PROVEEDOR" },
  { value: "5", label: "FAMILIAR" },
  { value: "6", label: "ADMINISTRATIVO" },
];

const PreRegistroForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const initialValues = {
    cedula: "",
    nombres: "",
    whatsapp: "",
    celular: "",
    correo: "",
    placa: "",
    fecha_inicio: "",
    fecha_final: "",
    nota: "",
    tipo_de_ingreso: "0",
    es_airbnb: false,
    acompanantes: [],
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    try {
      const dataToSend = {
        cedula: values.cedula,
        nombres: values.nombres,
        whatsapp: values.whatsapp,
        celular: values.celular,
        correo: values.correo,
        placa: values.placa || null,
        fecha_inicio: values.fecha_inicio,
        fecha_final: values.fecha_final,
        nota: values.nota || null,
        tipo_de_ingreso: values.tipo_de_ingreso,
        es_airbnb: values.es_airbnb,
        acompanantes: values.acompanantes || [],
      };

      await ResidenteService.createPreRegistro(dataToSend);
      notifySuccess("Pre-registro creado correctamente");
      resetForm();
      navigate("/pre-registros");
    } catch (error) {
      notifyError("Error al crear el pre-registro");
      handleErrors(error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Card className="mb-4">
        <Card.Header>
          <h4 className="card-title">Nuevo Pre-registro de Visitante</h4>
        </Card.Header>
        <Card.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, errors, touched, values, setFieldValue }) => (
              <Form>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Cédula <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Group controlId="cedula">
                        <Field
                          as={Form.Control}
                          type="text"
                          name="cedula"
                          placeholder="Número de cédula"
                          className={`form-control ${
                            errors.cedula && touched.cedula ? "is-invalid" : ""
                          }`}
                        />
                        <ErrorMessage
                          name="cedula"
                          component="div"
                          className="invalid-feedback"
                        />
                      </Form.Group>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Nombres <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Group controlId="nombres">
                        <Field
                          as={Form.Control}
                          type="text"
                          name="nombres"
                          placeholder="Nombres completos"
                          className={`form-control ${
                            errors.nombres && touched.nombres ? "is-invalid" : ""
                          }`}
                        />
                        <ErrorMessage
                          name="nombres"
                          component="div"
                          className="invalid-feedback"
                        />
                      </Form.Group>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Celular <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Group controlId="celular">
                        <Field
                          as={Form.Control}
                          type="text"
                          name="celular"
                          placeholder="573001234567"
                          className={`form-control ${
                            errors.celular && touched.celular ? "is-invalid" : ""
                          }`}
                        />
                        <ErrorMessage
                          name="celular"
                          component="div"
                          className="invalid-feedback"
                        />
                      </Form.Group>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        WhatsApp <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Group controlId="whatsapp">
                        <Field
                          as={Form.Control}
                          type="text"
                          name="whatsapp"
                          placeholder="573001234567"
                          className={`form-control ${
                            errors.whatsapp && touched.whatsapp ? "is-invalid" : ""
                          }`}
                        />
                        <ErrorMessage
                          name="whatsapp"
                          component="div"
                          className="invalid-feedback"
                        />
                      </Form.Group>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Correo <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Group controlId="correo">
                        <Field
                          as={Form.Control}
                          type="email"
                          name="correo"
                          placeholder="correo@ejemplo.com"
                          className={`form-control ${
                            errors.correo && touched.correo ? "is-invalid" : ""
                          }`}
                        />
                        <ErrorMessage
                          name="correo"
                          component="div"
                          className="invalid-feedback"
                        />
                      </Form.Group>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Tipo de Ingreso</Form.Label>
                      <Form.Group controlId="tipo_de_ingreso">
                        <Field
                          as="select"
                          name="tipo_de_ingreso"
                          className="form-control"
                        >
                          {tipoIngresoOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Field>
                      </Form.Group>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Fecha Inicio <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Group controlId="fecha_inicio">
                        <Field
                          as={Form.Control}
                          type="date"
                          name="fecha_inicio"
                          className={`form-control ${
                            errors.fecha_inicio && touched.fecha_inicio
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <ErrorMessage
                          name="fecha_inicio"
                          component="div"
                          className="invalid-feedback"
                        />
                      </Form.Group>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Fecha Final <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Group controlId="fecha_final">
                        <Field
                          as={Form.Control}
                          type="date"
                          name="fecha_final"
                          className={`form-control ${
                            errors.fecha_final && touched.fecha_final
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <ErrorMessage
                          name="fecha_final"
                          component="div"
                          className="invalid-feedback"
                        />
                      </Form.Group>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Placa (opcional)</Form.Label>
                      <Form.Group controlId="placa">
                        <Field
                          as={Form.Control}
                          type="text"
                          name="placa"
                          placeholder="ABC123"
                          className={`form-control ${
                            errors.placa && touched.placa ? "is-invalid" : ""
                          }`}
                        />
                        <ErrorMessage
                          name="placa"
                          component="div"
                          className="invalid-feedback"
                        />
                      </Form.Group>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mt-4">
                      <Form.Check
                        type="checkbox"
                        id="es_airbnb"
                        label="¿Es visitante de Airbnb?"
                      >
                        <Field
                          type="checkbox"
                          name="es_airbnb"
                          as={Form.Check.Input}
                        />
                        <Form.Check.Label>¿Es visitante de Airbnb?</Form.Check.Label>
                      </Form.Check>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Notas</Form.Label>
                      <Form.Group controlId="nota">
                        <Field
                          as={Form.Control}
                          as="textarea"
                          name="nota"
                          rows={3}
                          placeholder="Notas adicionales sobre el visitante"
                          className="form-control"
                        />
                      </Form.Group>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Acompañantes</Form.Label>
                      <FieldArray name="acompanantes">
                        {({ push, remove }) => (
                          <div>
                            {values.acompanantes.map((acompanante, index) => (
                              <div key={index} className="d-flex gap-2 mb-2">
                                <Field
                                  as={Form.Control}
                                  name={`acompanantes.${index}.cedula`}
                                  placeholder="Cédula"
                                  className="form-control"
                                  style={{ maxWidth: "150px" }}
                                />
                                <Field
                                  as={Form.Control}
                                  name={`acompanantes.${index}.nombres`}
                                  placeholder="Nombres"
                                  className="form-control"
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => remove(index)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => push({ cedula: "", nombres: "" })}
                            >
                              <i className="fas fa-plus"></i> Agregar acompañante
                            </button>
                          </div>
                        )}
                      </FieldArray>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-between mt-4">
                  <LinkDanger to="/pre-registros">
                    <i className="fas fa-arrow-left"></i> Volver
                  </LinkDanger>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate("/pre-registros")}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting || loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Guardando...
                        </>
                      ) : (
                        "Crear Pre-registro"
                      )}
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PreRegistroForm;
